import FetchClient, { type ApiResponse } from "./FetchClient";

/**
 * The one in-store deal a visitor sees in full after entering a ZIP: the best
 * find at the nearby stores, with its price, store, aisle and stock. Every
 * other local deal stays locked (see PublicPreviewService).
 */
export interface PublicFreeDeal {
  name: string;
  image: string;
  retailer: string;
  regularPrice: number;
  price: number;
  discountPercent: number;
  aisle?: string;
  quantity?: number;
  storeAddress?: string;
  distanceMiles?: number;
}

export interface PublicFreeDealResult {
  zipCode: string;
  freeDeal: PublicFreeDeal | null;
  totalDeals: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

const optionalString = (value: unknown) => (typeof value === "string" && value.trim() ? value.trim() : undefined);
const optionalNumber = (value: unknown) => (typeof value === "number" && Number.isFinite(value) && value > 0 ? value : undefined);

export function parsePublicFreeDeal(value: unknown): PublicFreeDeal | null {
  if (!isRecord(value)) return null;
  const { name, image, retailer, regularPrice, price, discountPercent } = value;
  if (
    typeof name !== "string" || !name.trim() ||
    typeof retailer !== "string" ||
    typeof price !== "number" || !(price > 0) ||
    typeof regularPrice !== "number" || !(regularPrice > price) ||
    typeof discountPercent !== "number"
  ) {
    return null;
  }
  return {
    name: name.trim(),
    image: typeof image === "string" ? image : "",
    retailer,
    regularPrice,
    price,
    discountPercent: Math.round(discountPercent),
    aisle: optionalString(value.aisle),
    quantity: optionalNumber(value.quantity),
    storeAddress: optionalString(value.storeAddress),
    distanceMiles: optionalNumber(value.distanceMiles),
  };
}

export async function getPublicFreeDeal(zipCode: string, retailer = "walmart"): Promise<PublicFreeDealResult> {
  const params = new URLSearchParams({ zipCode });
  if (retailer !== "walmart") params.set("retailer", retailer);
  const response = await FetchClient.get<ApiResponse<unknown>>(`/public/free-deal?${params.toString()}`, {}, "optional");
  const data = isRecord(response.data) ? response.data : {};
  const freeDeal = parsePublicFreeDeal(data.freeDeal);
  const totalDeals = typeof data.totalDeals === "number" && data.totalDeals > 0 ? Math.floor(data.totalDeals) : 0;
  return { zipCode, freeDeal: freeDeal && freeDeal.retailer === retailer ? freeDeal : null, totalDeals };
}
